import type { FC } from 'react';

import { MASCOT_COLORS, type MascotColor } from '#/lib/mascot';
import { cn } from '#/lib/utils';

/**
 * Where the eyes sit on the face. Side gazes reproduce the Figma
 * `agent_green_smile` asset (eyes toward the leading edge); `left` is that
 * asset mirrored, exactly as the Campaigns hero uses it. `up` keeps the eyes
 * centred and nudges them toward the exposed (top) edge.
 */
export type BlobGaze = 'center' | 'left' | 'right' | 'up';

/*
 * All geometry is in the Figma asset's own units (node 11063:2358,
 * `agent_green_smile`): the body path is a 237.5 × 211 tilted oval whose
 * bbox is the viewBox, so a host with the same aspect (135 × 120) renders it
 * at exactly that size.
 */
const BODY_PATH =
  'M248.976 133.842C239.525 207.489 182.866 231.355 117.805 223.158C52.7429 214.961 4.99575 169.388 13.3669 104.158C21.198 43.1362 79.4766 6.64494 144.538 14.8419C209.6 23.0388 257.347 68.6116 248.976 133.842Z';
const BODY_X = 12.4;
const BODY_Y = 13.7;
const BODY_W = 237.6;
const BODY_H = 211;
const CX = BODY_X + BODY_W / 2;
const CY = BODY_Y + BODY_H / 2;
const WIDE_VIEWBOX = `${BODY_X} ${BODY_Y} ${BODY_W} ${BODY_H}`;

/* Round roll body: same eye scale as the wide blob (board ratio r ≈ 6.6% ⌀). */
const ROUND_R = 111;
const ROUND_VIEWBOX = `${CX - ROUND_R} ${CY - ROUND_R} ${ROUND_R * 2} ${ROUND_R * 2}`;

/* Eyes — centred pair; the asset's own placement is this + 35.2 in x. */
const EYE_R = 14.678;
const EYE_SHIFT = 35.2;
const EYES: ReadonlyArray<{ cx: number; cy: number }> = [
  { cx: 124.914 - EYE_SHIFT, cy: 129.632 },
  { cx: 207.84 - EYE_SHIFT, cy: 137.078 },
];
const GAZE_OFFSET: Record<BlobGaze, { dx: number; dy: number }> = {
  center: { dx: 0, dy: 0 },
  left: { dx: EYE_SHIFT, dy: 0 }, // whole face is mirrored for `left`
  right: { dx: EYE_SHIFT, dy: 0 },
  up: { dx: 0, dy: -8 },
};

type Props = {
  color: MascotColor;
  /** When true, eyes blink (crossfade open ↔ closed). */
  blink?: boolean;
  /**
   * Use circular body for roll motion. Body and eyes are one rigid piece, so
   * a true circle keeps the silhouette stable while the whole thing spins.
   */
  round?: boolean;
  className?: string;
  gaze?: BlobGaze;
};

const Eye: FC<{ cx: number; cy: number; blink: boolean }> = ({
  cx,
  cy,
  blink,
}) => (
  <>
    <g className={cn(blink && 'blob-eye-open')}>
      <circle cx={cx} cy={cy} r={EYE_R} fill="#1C1C1E" />
      {/* Highlight: the asset's white pill, upper-right of the pupil. */}
      <rect
        x={cx + 5.4}
        y={cy - 6.6}
        width="4.717"
        height="7.076"
        rx="2.359"
        fill="#ffffff"
      />
    </g>
    {blink ? (
      <path
        className="blob-eye-closed"
        d={`M${cx - 23.5},${cy} Q${cx},${cy + 5.9} ${cx + 23.5},${cy}`}
        strokeWidth="4.2"
      />
    ) : null}
  </>
);

/**
 * Signature tilted-oval blob (or round roll body) with two eyes. Fill comes
 * from `--blob-color` / the color prop. Blink is a crossfade at a fixed
 * position — never a squash — matching the motion-board spec.
 */
export const BlobSvg: FC<Props> = ({
  color,
  blink = false,
  round = false,
  className,
  gaze = 'center',
}) => {
  const fill = MASCOT_COLORS[color];
  const { dx, dy } = GAZE_OFFSET[gaze];
  const mirrored = gaze === 'left';

  return (
    <svg
      className={cn('block overflow-visible', className)}
      viewBox={round ? ROUND_VIEWBOX : WIDE_VIEWBOX}
      width="100%"
      height="100%"
      aria-hidden="true"
      style={{ ['--blob-color' as string]: fill }}
    >
      <g
        transform={mirrored ? `translate(${CX * 2} 0) scale(-1 1)` : undefined}
      >
        {round ? (
          <circle
            className="fill-[var(--blob-color)]"
            cx={CX}
            cy={CY}
            r={ROUND_R}
          />
        ) : (
          <path className="fill-[var(--blob-color)]" d={BODY_PATH} />
        )}

        <g transform={`translate(${dx} ${dy})`}>
          {EYES.map((eye) => (
            <Eye key={eye.cx} cx={eye.cx} cy={eye.cy} blink={blink} />
          ))}
        </g>
      </g>
    </svg>
  );
};
